import {
    Component, ElementRef, Input, OnInit, ViewChild
} from '@angular/core';
import AudioTrackModel from '../../../../models/audio-track.model';
import {Store} from '@ngrx/store';
import {
    selectActualVideoState,
    selectCurrentTimeFromState,
    selectNavigateToTimeFromState,
    selectUserVolumeFromState
} from '../../../shared/stores/root-store/selectors/video.selector';
import {filter, map, mergeMap} from 'rxjs/operators';
import VideoState from '../../../shared/stores/root-store/video.state';
import {VideoStateEnum} from '../../../../enums/video-state.enum';
import {SetAudioTrackIsLoaded, SetAudioTrackIsPlaying} from '../../../shared/stores/root-store/actions/audio.action';

@Component({
    selector: 'eocm-audio-track',
    templateUrl: './audio-track.component.html',
    styleUrls: ['./audio-track.component.scss']
})
export class AudioTrackComponent implements OnInit {
    @ViewChild('audioSource', {
        static: true
    }) audioSource: ElementRef;

    @Input() audio: AudioTrackModel;
    time = 0;
    playCount = 0;
    isPaused = false;
    startTime: number;

    syncInterval: ReturnType<typeof setInterval>;
    syncTolerance = 0.1; // s

    private videoState: VideoStateEnum;
    private diffCount = 0;

    constructor(private store: Store<{ video: VideoState }>) {
    }

    ngOnInit() {
        this.store.select(selectCurrentTimeFromState)
            .pipe(
                mergeMap(time => {
                    this.time = time;
                    return this.store.select(selectActualVideoState);
                }),
                map(state => this.playOrPauseAudio(state))
            )
            .subscribe();

        this.store.select(selectUserVolumeFromState)
            .subscribe(volume => {
                this.audioSource.nativeElement.volume = this.calculateUserVolume(volume);
            });

        if (this.audio.synchronized) {
            this.store.select(selectNavigateToTimeFromState).pipe(
                filter(navTime => navTime !== 0),
                map(time => {
                    this.resetAudio();
                    this.time = time;

                    return time;
                }),
                filter(time => time > this.audio.time.start && time < this.audio.time.end),
                map(navTime => {
                    this.setCurrentTime(navTime);
                }))
                .subscribe();
        }
    }

    onLoadedData() {
        this.store.dispatch(SetAudioTrackIsLoaded({
            isLoaded: true, audioTrackId: this.audio.id
        }));
    }

    setCurrentTime(time) {
        this.store.dispatch(SetAudioTrackIsLoaded({
            isLoaded: false, audioTrackId: this.audio.id
        }));
        this.audioSource.nativeElement.currentTime = time - this.audio.time.start;
    }

    calculateUserVolume(userVolume) {
        return this.audio.volume * userVolume / 100;
    }

    shouldPlayAudio() {
        return this.time >= this.audio.time.start && this.time < this.audio.time.end;
    }

    async playOrPauseAudio(videoState: VideoStateEnum) {
        this.videoState = videoState;

        if (videoState === VideoStateEnum.Ended) {
            this.resetAudio();

            return;
        }

        if (videoState === VideoStateEnum.Interaction && this.shouldPlayAudio() && !this.audio.synchronized) {
            await this.playAudio();

            return;
        }

        if (this.shouldPlayAudio()) {
            videoState === VideoStateEnum.Playing ? await this.playAudio() : this.pauseAudio();

            if (!this.audio.isPlaying) {
                this.audio.isPlaying = true;
                this.store.dispatch(SetAudioTrackIsPlaying({
                    isPlaying: true, audioTrackId: this.audio.id
                }));
            }
        } else if (this.audio.isPlaying) {
            this.pauseAudio();
            this.audio.isPlaying = false;
            this.store.dispatch(SetAudioTrackIsPlaying({
                isPlaying: false, audioTrackId: this.audio.id
            }));
        }
    }

    private async playAudio() {
        if (this.isPaused) {
            try {
                await this.nativePlay();
            } catch (e) {
                // no action here
            }

            return;
        }

        if (this.playCount > 0 && this.audio.synchronized) {
            return;
        }

        this.playCount++;
        await this.nativePlay();
    }

    private async nativePlay() {
        if (this.audio.synchronized && !this.syncInterval) {
            this.syncInterval = setInterval(() => this.syncTime(), 25);
        }

        this.isPaused = false;
        this.startTime = this.time - this.audioSource.nativeElement.currentTime;

        return await this.audioSource.nativeElement.play();
    }

    private pauseAudio() {
        this.clearSyncInterval();

        if (this.isPaused) {
            return;
        }

        this.isPaused = true;
        this.audioSource.nativeElement.pause();
    }

    private resetAudio() {
        this.pauseAudio();

        this.audioSource.nativeElement.currentTime = 0;
        this.playCount = 0;
        this.diffCount = 0;
    }

    private syncTime() {
        const videoTime = this.time;
        const audioTime = this.startTime + this.audioSource.nativeElement.currentTime;
        const diff = audioTime - videoTime;

        if (diff > this.syncTolerance) {
            this.audioSource.nativeElement.playbackRate = 0.9;
        } else if(diff < -this.syncTolerance){
            this.audioSource.nativeElement.playbackRate = 1.1;
        } else {
            this.audioSource.nativeElement.playbackRate = 1.0;
        }
    }

    progressEvent() {
        this.checkBuffering();
    }

    updateTime() {
        this.checkBuffering();
    }

    private checkBuffering() {
        if (this.audio.isPlaying && this.audioSource.nativeElement.readyState < HTMLMediaElement.HAVE_ENOUGH_DATA) {
            this.store.dispatch(SetAudioTrackIsLoaded({
                isLoaded: false, audioTrackId: this.audio.id
            }));
            this.audio.loaded = false;

            return false;
        } else if (!this.audio.loaded) {
            this.store.dispatch(SetAudioTrackIsLoaded({
                isLoaded: true, audioTrackId: this.audio.id
            }));
        }

        return true;
    }

    private clearSyncInterval() {
        if (this.syncInterval) {
            clearInterval(this.syncInterval);
            this.syncInterval = null;
        }
    }
}
